import React, { useCallback, useEffect, useMemo, useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { Box } from "@mui/material";

import { ButtonVariant } from "Constant/button";
import { useDispatch, useSelector } from "react-redux";

import { deleteNotification } from "helpers/notification/deleteNotification";

import EmptyState from "components/atoms/EmptyState";
import PageContainer from "components/atoms/PageContainer";
import { ConfirmationModal } from "components/organisms/ConfirmationModal";
import GenericTable from "components/organisms/GenericTable";
import SwalAlertComp from "components/SwalAlert";

import {
    getNotificationsRedux,
    getNotificationsSummary,
    markAsReadBulk,
} from "../../../../redux/slices/notifications/notifications.asyncActions";
import { createNotificationsListColumns } from "./NotificationsAll.helpers";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const NotificationsAll = () => {
    const dispatch = useDispatch();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const [deleteSource, setDeleteSource] = useState(null); // "row" | "bulk"
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [markingId, setMarkingId] = useState(null);
    const [bulkMarkPending, setBulkMarkPending] = useState(false);
    const [bulkDeletePending, setBulkDeletePending] = useState(false);
    const actionsBusy = bulkMarkPending || bulkDeletePending;

    const { notifications, status } = useSelector(
        (state) => state.notificationsState,
    );

    const pageData = useMemo(
        () => notifications?.notifications || [],
        [notifications],
    );

    const pageIds = useMemo(
        () => pageData.map((notification) => notification.id),
        [pageData],
    );

    const totalItems = notifications?.pagination?.total || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);

    useEffect(() => {
        dispatch(getNotificationsRedux({ page, per_page: pageSize }));
    }, [page, pageSize, dispatch]);

    useEffect(() => {
        setSelectedIds([]);
    }, [page, pageSize]);

    const refreshListAndSummary = useCallback(() => {
        return dispatch(
            getNotificationsRedux({ page, per_page: pageSize }),
        ).then(() => dispatch(getNotificationsSummary()));
    }, [dispatch, page, pageSize]);

    const handleToggleRow = useCallback((id) => {
        setSelectedIds((prev) =>
            prev.includes(id)
                ? prev.filter((item) => item !== id)
                : [...prev, id],
        );
    }, []);

    const handleToggleAll = useCallback(() => {
        setSelectedIds((prev) => {
            const allSelected =
                pageIds.length > 0 && pageIds.every((id) => prev.includes(id));
            return allSelected ? [] : [...pageIds];
        });
    }, [pageIds]);

    const handleMarkAsRead = useCallback(
        async (ids, { fromToolbar = false } = {}) => {
            const notificationIds = (Array.isArray(ids) ? ids : [ids]).filter(
                Boolean,
            );
            if (notificationIds.length === 0) return;

            if (fromToolbar) {
                setBulkMarkPending(true);
            } else {
                setMarkingId(notificationIds[0]);
            }

            try {
                await dispatch(markAsReadBulk(notificationIds)).unwrap();
                if (!fromToolbar) {
                    setSelectedIds((prev) =>
                        prev.filter((id) => !notificationIds.includes(id)),
                    );
                }
            } catch (error) {
                SwalAlertComp({
                    title: "Failed",
                    message: "Error while marking notification(s) as read",
                    icon: "error",
                });
            } finally {
                await refreshListAndSummary();

                if (fromToolbar) {
                    setSelectedIds([]);
                }
                setBulkMarkPending(false);
                setMarkingId(null);
            }
        },
        [dispatch, refreshListAndSummary],
    );

    const handleRequestDelete = useCallback((id) => {
        setPendingDeleteIds([id]);
        setDeleteSource("row");
        setConfirmOpen(true);
    }, []);

    const handleRequestBulkDelete = useCallback(() => {
        if (selectedIds.length === 0) return;
        setPendingDeleteIds([...selectedIds]);
        setDeleteSource("bulk");
        setConfirmOpen(true);
    }, [selectedIds]);

    const handleConfirmDelete = useCallback(async () => {
        const idsToDelete = [...pendingDeleteIds];
        const source = deleteSource;
        setConfirmOpen(false);
        setPendingDeleteIds([]);
        setDeleteSource(null);

        if (idsToDelete.length === 0) return;

        if (source === "bulk") {
            setBulkDeletePending(true);
        } else {
            setDeletingId(idsToDelete[0]);
        }

        let successCount = 0;
        let lastError = "";

        for (const id of idsToDelete) {
            const response = await deleteNotification(id);
            if (response.status) {
                successCount += 1;
            } else {
                lastError = response.message || lastError;
            }
        }

        setBulkDeletePending(false);
        setDeletingId(null);

        if (source === "bulk") {
            setSelectedIds([]);
        }

        if (successCount > 0) {
            SwalAlertComp({
                title: "Deleted",
                message:
                    successCount === 1
                        ? "Notification deleted successfully"
                        : `${successCount} notifications deleted successfully`,
                icon: "success",
                timer: 2000,
            });
            if (source !== "bulk") {
                setSelectedIds((prev) =>
                    prev.filter((id) => !idsToDelete.includes(id)),
                );
            }
        }

        await refreshListAndSummary();

        if (successCount < idsToDelete.length) {
            SwalAlertComp({
                title: "Failed",
                message:
                    lastError ||
                    "Error while deleting one or more notifications",
                icon: "error",
            });
        }
    }, [pendingDeleteIds, deleteSource, refreshListAndSummary]);

    const columns = useMemo(
        () =>
            createNotificationsListColumns({
                selectedIds,
                pageIds,
                onToggleRow: handleToggleRow,
                onToggleAll: handleToggleAll,
                onMarkAsRead: handleMarkAsRead,
                onRequestDelete: handleRequestDelete,
                deletingId,
                markingId,
                actionsDisabled: actionsBusy,
            }),
        [
            selectedIds,
            pageIds,
            handleToggleRow,
            handleToggleAll,
            handleMarkAsRead,
            handleRequestDelete,
            deletingId,
            markingId,
            actionsBusy,
        ],
    );

    const selectedUnreadIds = useMemo(
        () =>
            selectedIds.filter((id) => {
                const row = pageData.find(
                    (notification) => notification.id === id,
                );
                return row && !row.read_at;
            }),
        [selectedIds, pageData],
    );

    const actionButtons = useMemo(() => {
        if (selectedIds.length === 0) return undefined;

        return [
            {
                key: "mark-read",
                name: `Mark as Read (${selectedUnreadIds.length})`,
                icon: <DoneAllIcon fontSize="small" />,
                disabled: actionsBusy || selectedUnreadIds.length === 0,
                isLoading: bulkMarkPending,
                onClick: () =>
                    handleMarkAsRead(selectedUnreadIds, { fromToolbar: true }),
            },
            {
                key: "delete",
                name: `Delete (${selectedIds.length})`,
                icon: <DeleteOutlineIcon fontSize="small" />,
                variant: ButtonVariant.DANGER_OUTLINED,
                disabled: actionsBusy,
                isLoading: bulkDeletePending,
                onClick: handleRequestBulkDelete,
            },
        ];
    }, [
        selectedIds,
        selectedUnreadIds,
        actionsBusy,
        bulkMarkPending,
        bulkDeletePending,
        handleMarkAsRead,
        handleRequestBulkDelete,
    ]);

    return (
        <PageContainer
            title="Notifications"
            subtitle="Review notifications, mark them as read, or delete them in bulk."
            showBackButton={false}
            variant="list"
            loading={status === "loading" && pageData.length === 0}
        >
            <Box
                sx={{
                    maxWidth: "100%",
                    minWidth: 0,
                    width: "100%",
                    position: "relative",
                }}
            >
                <GenericTable
                    columns={columns}
                    rows={pageData}
                    getRowKey={(row) => row.id}
                    emptyState={
                        <EmptyState
                            icon={<NotificationsNoneOutlinedIcon />}
                            title="No notifications yet"
                            description="When you receive notifications, they will appear here."
                        />
                    }
                    loading={false}
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    onPageChange={setPage}
                    pageSize={pageSize}
                    onPageSizeChange={(nextSize) => {
                        setPageSize(nextSize);
                        setPage(1);
                    }}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    isRowSelected={(row) => selectedIds.includes(row.id)}
                    selectedRowVariant="black"
                    ellipsis={false}
                    columnToggle
                    actionButtons={actionButtons}
                    defaultHiddenColumns={[]}
                    tableName="notifications"
                />
            </Box>

            <ConfirmationModal
                open={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                    setPendingDeleteIds([]);
                    setDeleteSource(null);
                }}
                onConfirm={handleConfirmDelete}
                title="Are you sure?"
                message={
                    pendingDeleteIds.length > 1
                        ? `Delete ${pendingDeleteIds.length} notifications? This action cannot be undone.`
                        : "This action cannot be undone."
                }
                confirmColor="error"
            />
        </PageContainer>
    );
};

export default NotificationsAll;
